/**
 * Licence changes from the state register (issue #635) — client.
 *
 * Two GET-only reads plus the audited CSV download. No write here: the
 * "Done in CE21" tick goes through `createCaseAcknowledgement` in
 * `api/outreach.ts` with `queue_key: STATE_LICENSE_QUEUE_KEY`, the same
 * write every other desk queue uses. Nothing on this rail can touch CE21.
 */
import { apiRequest } from "./client";
import { triggerDownload } from "./audienceReview";
import { resolveTenant } from "../lib/tenant";
import type { DeskReviewState } from "./desk.types";
import type { StateLicenseQueueResponse, StateLicenseSummary } from "./stateLicense.types";

const TENANT_HEADER = "X-Tenant";

/** Backend page-size default (`apps.desk.statelicense.DEFAULT_PAGE_LIMIT`). */
export const STATE_LICENSE_DEFAULT_LIMIT = 50;

export interface StateLicenseQueueQuery {
  review_state?: DeskReviewState;
  limit?: number;
  offset?: number;
}

function queueParams(query: StateLicenseQueueQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.review_state) params.set("review_state", query.review_state);
  if (query.limit != null) params.set("limit", String(query.limit));
  if (query.offset) params.set("offset", String(query.offset));
  return params;
}

/** `GET /api/desk/queues/state-license` — the queue with review state. */
export function getStateLicenseQueue(
  query: StateLicenseQueueQuery = {},
  signal?: AbortSignal,
): Promise<StateLicenseQueueResponse> {
  const qs = queueParams(query).toString();
  return apiRequest<StateLicenseQueueResponse>(
    `/desk/queues/state-license${qs ? `?${qs}` : ""}`,
    { signal },
  );
}

/** `GET /api/insights/state-license-summary` — counts for the Today row. */
export function getStateLicenseSummary(): Promise<StateLicenseSummary> {
  return apiRequest<StateLicenseSummary>("/insights/state-license-summary");
}

/**
 * Download the (optionally narrowed) queue as CSV — the same rows the table
 * shows, through the audited export rail. Returns the backend's row total.
 */
export async function downloadStateLicenseCsv(
  query: Pick<StateLicenseQueueQuery, "review_state"> = {},
): Promise<{ rowTotal: number }> {
  const params = queueParams(query);
  params.set("export", "csv");
  const response = await fetch(`/api/desk/queues/state-license?${params.toString()}`, {
    method: "GET",
    headers: new Headers({ [TENANT_HEADER]: resolveTenant() }),
    credentials: "include",
  });
  if (!response.ok) {
    const err = new Error(`Export failed: ${response.status}`) as Error & { status: number };
    err.status = response.status;
    throw err;
  }
  const rowTotal = Number(response.headers.get("X-Row-Total") ?? "0");
  triggerDownload(await response.blob(), "licence-changes-from-state-list.csv");
  return { rowTotal };
}
